import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePageTitle } from "@/hooks/use-page-title";
import { ArrowUpRight, Check, CreditCard, Layers3, WalletCards } from "lucide-react";

const MERCURY_URL = "https://mercury.com/r/padpulse-llc";

const benefits = [
  {
    icon: CreditCard,
    title: "A card for every property",
    description: "Create individual virtual cards to separate spending for each property.",
  },
  {
    icon: Layers3,
    title: "Expenses that stay organized",
    description: "Keep property purchases easier to identify and review instead of sorting one mixed pile.",
  },
  {
    icon: WalletCards,
    title: "Portfolio-wide spending control",
    description: "See spending across your portfolio and manage property expenses with more clarity.",
  },
];

export default function AppResourceMercury() {
  usePageTitle("Earn $250");

  return (
    <AppLayout>
      <main className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-9">
        <header className="mb-6 sm:mb-8">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-brand">
            PadPulse resource
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            Earn $250
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
            A business banking option for keeping property expenses clear and your portfolio spending in view.
          </p>
        </header>

        <Card className="overflow-hidden border-brand/20">
          <CardContent className="p-0">
            <section
              aria-labelledby="mercury-offer-heading"
              className="relative isolate overflow-hidden bg-brand-subtle px-5 py-6 dark:bg-brand-muted/40 sm:px-8 sm:py-8"
            >
              <div aria-hidden="true" className="absolute -right-10 -top-16 -z-10 h-56 w-56 rounded-full border-[28px] border-brand/5 sm:h-72 sm:w-72" />
              <div className="max-w-2xl">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand/15 bg-card/70 px-3 py-1.5 text-xs font-medium text-brand-muted-foreground">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand/10">
                    <Check aria-hidden="true" className="h-3 w-3" />
                  </span>
                  Business banking for property operators
                </div>
                <h2 id="mercury-offer-heading" className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                  Mercury business banking
                </h2>
                <p className="mt-2 max-w-xl text-sm leading-6 text-muted-foreground">
                  Mercury makes it easy to create individual virtual cards per property — helping keep expenses organized and giving you control across your entire portfolio.
                </p>
                <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="flex-1 rounded-lg border border-brand/15 bg-card/80 px-4 py-3">
                    <p className="text-sm font-semibold leading-5 text-foreground">
                      Get a $250 bonus when you deposit $10,000 within 90 days of opening the account.
                    </p>
                  </div>
                  <Button asChild size="lg" className="shrink-0">
                    <a href={MERCURY_URL} target="_blank" rel="noopener noreferrer">
                      Create Account
                      <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
                <p className="mt-3 text-xs leading-5 text-muted-foreground">
                  Mercury offer terms apply.
                </p>
              </div>
            </section>

            <section aria-labelledby="benefits-heading" className="px-5 py-5 sm:px-8 sm:py-6">
              <h2 id="benefits-heading" className="mb-4 text-sm font-semibold text-foreground">
                Built for a multi-property operation
              </h2>
              <ul className="grid gap-4 sm:grid-cols-3">
                {benefits.map(({ icon: Icon, title, description }) => (
                  <li key={title} className="flex gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-muted text-brand-muted-foreground">
                      <Icon aria-hidden="true" className="h-4 w-4" />
                    </span>
                    <div>
                      <h3 className="text-sm font-medium text-foreground">{title}</h3>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">{description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </CardContent>
        </Card>

        <p className="mt-4 max-w-3xl text-xs leading-5 text-muted-foreground">
          Affiliate disclosure: PadPulse may receive compensation if you open an account through this link. See Mercury for current eligibility and offer details.
        </p>
      </main>
    </AppLayout>
  );
}